import { store } from '@msc-b2c/shared-utils';
import { getAllConsentStates, getDidomi } from './consentBridge';
import { getAllConfigs, __resetConfigCache } from './getKeyConfig';
import { mscStorage } from './mscStorage';
import { normalizeWebStorageGetItem } from './normalizeWebStorageGetItem';

let isInitialized = false;
let isSubscribed = false;
let hasSeenComplianceConfig = false;

type DidomiReadyCallback = () => void;

function onDidomiReady(cb: DidomiReadyCallback): void {
  // If Didomi is already available — run now
  if (getDidomi()) {
    cb();
    return;
  }

  // If not ready yet — enqueue into didomiOnReady
  try {
    const w = globalThis.window as unknown as { didomiOnReady?: DidomiReadyCallback[] };
    if (!Array.isArray(w.didomiOnReady)) {
      w.didomiOnReady = [];
    }
    w.didomiOnReady.push(cb);
  } catch {
    // No window access / unexpected environment — ignore
    console.log('error subscribing to didomi ready', cb);
  }
}

function subscribeToStoreConfig(): void {
  const enforceWithConfig = (): void => {
    const json = store.getState().genericConfigs.configs.b2cCommerceConfig.cookieComplianceJson;
    if (json?.trim() && !hasSeenComplianceConfig) {
      hasSeenComplianceConfig = true;
      __resetConfigCache();
      onDidomiReady(() => mscStorage.enforce());
    }
  };
  enforceWithConfig();
  store.subscribe(enforceWithConfig);
}

function subscribeToConsentChanges(): void {
  if (isSubscribed) return;

  onDidomiReady(() => {
    if (isSubscribed) return;

    try {
      const didomi = getDidomi();
      if (didomi?.on) {
        didomi.on('consent.changed', () => mscStorage.onConsentChanged());
        isSubscribed = true;
      }
    } catch {
      // Didomi not available - consent changes won't trigger cleanup
      // Data will still be cleaned on next page load via enforce()
      console.log('error subscribing to consent changes');
    }
  });
}

function runEnforce(): void {
  onDidomiReady(() => {
    try {
      mscStorage.enforce();
    } catch {
      // Didomi not available - consent changes won't trigger cleanup
      // Data will still be cleaned on next page load via enforce()
    }
  });
}

/** Exposes debug API: __mscStorage, __mscStorageConfig, __mscStorageConsent */
function exposeDebugApi(): void {
  if (globalThis.window === undefined) return;

  const w = globalThis.window as unknown as Record<string, unknown>;
  w.__mscStorage = mscStorage;
  w.__mscStorageConfig = getAllConfigs();
  w.__mscStorageConsent = getAllConsentStates;
}

/** Initializes MSC Storage Compliance module (AC5.1: cleanup + consent subscription) */
export function initMscStorageCompliance(): void {
  if (isInitialized) return;
  isInitialized = true;

  normalizeWebStorageGetItem();
  subscribeToConsentChanges();
  runEnforce();
  subscribeToStoreConfig();
  exposeDebugApi();
}
